import * as React from 'react';
import { observer } from 'mobx-react';
import './page-template.scss';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import { useMemo } from 'react';
import { ITenantConfig } from '../../../domains/tenant-config';

interface ILink {
  url: string;
  key: string;
}

interface IProps {
  titleElement: React.JSX.Element;
  contentElement: React.JSX.Element;
  tenantConfig?: ITenantConfig;
}

export const PageTemplate = observer((props: IProps) => {
  const { t } = useTranslation('', { keyPrefix: 'PAGE_TEMPLATE' });
  const { titleElement, contentElement, tenantConfig } = props;
  const { tenantStore } = useStore();
  const { config } = tenantStore;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const additionalLinks = useMemo(() => {
    const links: ILink[] = [];
    if (config.supportUrl) links.push({ url: config.supportUrl, key: t('SUPPORT') });
    if (config.termsAndConditions) links.push({ url: config.termsAndConditions, key: t('TERMS_OF_USE') });
    if (config.privacyPolicyUrl) links.push({ url: config.privacyPolicyUrl, key: t('PRIVACY_POLICY') });
    if (config.newsUrl) links.push({ url: config.newsUrl, key: t('NEWS') });
    if (config.blogUrl) links.push({ url: config.blogUrl, key: t('BLOG') });
    return links;
  }, [config.supportUrl, t, config.termsAndConditions, config.privacyPolicyUrl, config.newsUrl]);

  const socialIcons = useMemo(() => {
    const icons: ILink[] = [];
    if (config.youtubeUrl) icons.push({ url: config.youtubeUrl, key: 'youtube-icon' });
    if (config.instagramUrl) icons.push({ url: config.instagramUrl, key: 'instagram-icon' });
    if (config.facebookUrl) icons.push({ url: config.facebookUrl, key: 'facebook-icon' });
    if (config.twitterUrl) icons.push({ url: config.twitterUrl, key: 'twitter-icon' });
    if (config.linkedinUrl) icons.push({ url: config.linkedinUrl, key: 'linkedin-icon' });
    return icons;
  }, [config.youtubeUrl, config.instagramUrl, config.facebookUrl, config.twitterUrl, config.linkedinUrl]);

  return (
    <div className="container-template">
      <div className="template-body">
        <div className="title d-flex">{titleElement}</div>

        <div className="container-grey">{contentElement}</div>

        <ul className="links d-flex">
          <li id="li_net2phone_url">
            <a href={config.net2PhoneUrl} target="_blank">
              {t('NET2PHONE')}
            </a>
          </li>
          {additionalLinks.map(link => (
            <li key={link.key}>
              <span>&#8226;</span>
              <a href={link.url} target="_blank">
                {link.key}
              </a>
            </li>
          ))}
        </ul>

        <ul className="social-icons d-flex">
          {socialIcons.map(icon => (
            <li key={icon.key}>
              <a href={icon.url} className={icon.key} target="_blank" aria-label={icon.key}></a>
            </li>
          ))}
        </ul>
      </div>
      <div className="template-inner">
        <div className="container">
          <p className="text-right">
            <small>
              {config.assistancePhoneNumber && t('CALL_NUMBER', { number: config.assistancePhoneNumber })}
              <span> </span>
              {t('COPYRIGHT', { currentYear })}
            </small>
          </p>
        </div>
      </div>
    </div>
  );
});
